import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Save, ShoppingCart, Trash2 } from 'lucide-react';

import PageHeader from '../components/PageHeader.jsx';
import SaveBar, { SaveButton } from '../components/SaveBar.jsx';
import FormField, { TextInput } from '../components/FormField.jsx';
import SearchableSelect from '../components/SearchableSelect.jsx';
import { listBatches } from '../api/batches';
import { getCustomer, listCustomers } from '../api/customers';
import { listProducts } from '../api/products';
import { createSale } from '../api/sales';
import { VAT_RATE } from '../constants/vat';

/**
 * New Sale flow.
 *
 * UX:
 *   1. Pick customer (full-screen searchable picker).
 *   2. Add line items: pick product → set quantity + unit price.
 *   3. Optional shipping amount.
 *   4. Review totals (VAT-exclusive subtotal, VAT, total).
 *   5. Submit → /sales (creates a draft).
 *
 * VAT convention matches the backend: unit_price is VAT-exclusive; backend
 * computes total_amount as (subtotal + shipping) * 1.155.
 */
export default function NewSale() {
  const navigate = useNavigate();
  const location = useLocation();

  const [customer, setCustomer] = useState(null);
  const [lines, setLines] = useState([]); // [{tempId, product, quantity, unit_price}]
  const [shipping, setShipping] = useState('0');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Inline-add flash from NewCustomer return (e.g. "Added Acme Co").
  const [flash, setFlash] = useState(location.state?.flash || null);
  // Synchronous guard — closes the ~50-100ms gap between tap and the
  // `submitting` state update where a double-tap could fire two
  // requests on a slow network. The ref flips immediately on the
  // first call; the second call sees it set and bails before any
  // POST happens.
  const submitInFlight = useRef(false);

  // Inline-add return: NewCustomer navigated back here with
  // `newCustomerId` in location.state. Fetch + pre-select. We clear
  // location.state afterwards so the effect doesn't re-fire.
  useEffect(() => {
    const newId = location.state?.newCustomerId;
    if (!newId) return;
    let cancelled = false;
    getCustomer(newId)
      .then((c) => {
        if (cancelled) return;
        setCustomer({
          id: c.id,
          name: c.name,
          sublabel: [c.contact_name, c.city].filter(Boolean).join(' · '),
        });
      })
      .catch(() => {
        // Best-effort — rep can re-pick from the list if needed.
      });
    navigate(location.pathname, { replace: true, state: null });
    return () => { cancelled = true; };
  }, [location.state, location.pathname, navigate]);

  const loadCustomerOptions = useCallback(async (q) => {
    const data = await listCustomers({ pageSize: 30, search: q || undefined });
    return data.items.map((c) => ({
      id: c.id,
      name: c.name,
      sublabel: [c.contact_name, c.city].filter(Boolean).join(' · '),
    }));
  }, []);

  const loadProductOptions = useCallback(async (q) => {
    const data = await listProducts({ pageSize: 30, search: q || undefined });
    return data.items.map((p) => ({
      id: p.id,
      name: p.name,
      sublabel: [p.sku, p.total_stock != null ? `${p.total_stock} in stock` : null]
        .filter(Boolean)
        .join(' · '),
      total_stock: p.total_stock,
    }));
  }, []);

  /**
   * Add a product as a new line item, then async-fetch its in-stock batches.
   *
   * Sales line items must be allocated against a batch (see
   * backend/app/schemas/sale.py — `batch_id` is required), so once a product
   * is picked we list its batches with available stock, sort FIFO (oldest
   * batch first; lowest id), and pre-select the first one. The user can
   * change the batch via a per-line picker. If the product has no batches
   * with stock, the line surfaces an error and submission is blocked.
   */
  async function addLine(product) {
    const tempId = Date.now() + Math.random();
    // Optimistic insert; batches resolve asynchronously below.
    setLines((curr) => [
      ...curr,
      {
        tempId,
        product,
        quantity: '1',
        unit_price: '0',
        batches: [],
        batchId: null,
        batchesLoading: true,
        batchError: null,
      },
    ]);

    try {
      const batches = await listBatches({ productId: product.id, hasStock: true });
      // FIFO: lowest id (oldest) first; backend returns desc by id.
      const sorted = [...batches].sort((a, b) => a.id - b.id);
      setLines((curr) =>
        curr.map((l) =>
          l.tempId === tempId
            ? {
                ...l,
                batches: sorted,
                batchId: sorted[0]?.id ?? null,
                batchesLoading: false,
                batchError: sorted.length === 0 ? 'No stock available for this product' : null,
              }
            : l,
        ),
      );
    } catch (err) {
      setLines((curr) =>
        curr.map((l) =>
          l.tempId === tempId
            ? {
                ...l,
                batchesLoading: false,
                batchError: err.response?.data?.detail || 'Failed to load batches',
              }
            : l,
        ),
      );
    }
  }

  function updateLine(tempId, patch) {
    setLines((curr) => curr.map((l) => (l.tempId === tempId ? { ...l, ...patch } : l)));
  }

  function removeLine(tempId) {
    setLines((curr) => curr.filter((l) => l.tempId !== tempId));
  }

  const subtotal =
    lines.reduce(
      (acc, l) => acc + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0),
      0,
    ) + (Number(shipping) || 0);
  const vat = subtotal * VAT_RATE;
  const total = subtotal + vat;

  async function onSubmit(e) {
    e.preventDefault();
    if (submitInFlight.current) return;
    if (!customer) {
      setError('Pick a customer first.');
      return;
    }
    if (lines.length === 0) {
      setError('Add at least one line item.');
      return;
    }
    // Every line must have a batch — backend requires batch_id per line item.
    const linesMissingBatch = lines.filter((l) => !l.batchId);
    if (linesMissingBatch.length > 0) {
      const stillLoading = linesMissingBatch.some((l) => l.batchesLoading);
      setError(
        stillLoading
          ? 'Still loading batch info — give it a moment then try again.'
          : 'Pick a batch for every line item (some products have no stock available).',
      );
      return;
    }
    submitInFlight.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        customer_id: customer.id,
        line_items: lines.map((l) => ({
          product_id: l.product.id,
          batch_id: l.batchId,
          quantity: Number(l.quantity),
          unit_price: Number(l.unit_price),
        })),
        shipping_amount: Number(shipping) || 0,
        notes: notes || null,
      };
      const sale = await createSale(payload);
      navigate(`/sales/${sale.id}`, {
        replace: true,
        state: { flash: `Sale ${sale.invoice_number ?? '#' + sale.id} saved as draft` },
      });
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to save sale');
    } finally {
      setSubmitting(false);
      submitInFlight.current = false;
    }
  }

  return (
    <main className="min-h-full px-4 pb-28">
      <PageHeader
        title="New Sale"
        subtitle="Create a draft invoice"
        icon={ShoppingCart}
      />

      {flash && (
        <div
          role="status"
          className="mb-3 mb-card bg-green-50 border-green-200 text-small text-green-900"
        >
          <div className="flex items-center justify-between gap-2">
            <span>{flash}</span>
            <button
              type="button"
              onClick={() => setFlash(null)}
              aria-label="Dismiss"
              className="text-green-700 -mr-1 px-2"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-4">
        <FormField label="Customer">
          <SearchableSelect
            value={customer}
            onChange={setCustomer}
            placeholder="Pick a customer"
            loadOptions={loadCustomerOptions}
            labelOf={(c) => c.name}
            emptyAction={{
              label: '+ Add new customer',
              onClick: () =>
                navigate('/customers/new', {
                  state: { returnTo: '/sales/new' },
                }),
            }}
          />
        </FormField>

        <div>
          <p className="text-small font-medium text-gray-700 mb-1.5">Line items</p>
          <div className="space-y-2">
            {lines.map((l) => (
              <div key={l.tempId} className="mb-card space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-body font-medium text-gray-900 truncate">
                      {l.product.name}
                    </p>
                    {l.product.sublabel && (
                      <p className="text-caption text-gray-500 truncate">
                        {l.product.sublabel}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeLine(l.tempId)}
                    aria-label="Remove line"
                    className="min-h-touch min-w-touch flex items-center justify-center text-danger"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <FormField label="Qty">
                    <TextInput
                      type="number"
                      inputMode="decimal"
                      min="1"
                      step="1"
                      value={l.quantity}
                      onChange={(e) => updateLine(l.tempId, { quantity: e.target.value })}
                    />
                  </FormField>
                  <FormField label="Unit price (ex VAT)">
                    <TextInput
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.01"
                      value={l.unit_price}
                      onChange={(e) => updateLine(l.tempId, { unit_price: e.target.value })}
                    />
                  </FormField>
                </div>

                {/* Batch picker — required by the backend (SaleLineItemCreate.batch_id).
                    FIFO-preselected; user can change if multiple batches are
                    in stock. Single-batch case shows it as static text rather
                    than a redundant dropdown. */}
                {l.batchesLoading ? (
                  <p className="text-caption text-gray-500">Loading batches…</p>
                ) : l.batchError ? (
                  <p className="text-caption text-danger">{l.batchError}</p>
                ) : l.batches.length === 1 ? (
                  <p className="text-caption text-gray-500">
                    Batch: <span className="font-medium text-gray-700">{l.batches[0].order_number}</span>
                    {' · '}
                    {l.batches[0].available_qty} available
                  </p>
                ) : (
                  <FormField label="Batch">
                    <select
                      value={l.batchId ?? ''}
                      onChange={(e) =>
                        updateLine(l.tempId, { batchId: Number(e.target.value) })
                      }
                      className="w-full h-10 px-3 border border-gray-300 rounded-md text-body bg-surface hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    >
                      {l.batches.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.order_number} — {b.available_qty} available
                        </option>
                      ))}
                    </select>
                  </FormField>
                )}

                <p className="text-right text-small text-gray-500">
                  Line total: {((Number(l.quantity) || 0) * (Number(l.unit_price) || 0)).toFixed(2)}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-2">
            <SearchableSelect
              value={null}
              onChange={addLine}
              placeholder="+ Add product"
              loadOptions={loadProductOptions}
              labelOf={(p) => p.name}
            />
          </div>
        </div>

        <FormField label="Shipping (ex VAT)" hint="0 if none">
          <TextInput
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={shipping}
            onChange={(e) => setShipping(e.target.value)}
          />
        </FormField>

        <FormField label="Notes (optional)">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full px-3 py-3 border border-gray-300 rounded-md text-body hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          />
        </FormField>

        {/* Totals */}
        <section className="mb-card space-y-1">
          <div className="flex justify-between text-small text-gray-600">
            <span>Subtotal (ex VAT)</span>
            <span>{subtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-small text-gray-600">
            <span>VAT (15.5%)</span>
            <span>{vat.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-title font-semibold text-gray-900 pt-1 border-t border-gray-200">
            <span>Total</span>
            <span>{total.toFixed(2)}</span>
          </div>
        </section>

        {error && <p role="alert" className="text-small text-danger">{error}</p>}

        <SaveBar>
          <SaveButton type="submit" icon={Save} loading={submitting} disabled={submitting}>
            {submitting ? 'Saving…' : 'Save as Draft'}
          </SaveButton>
        </SaveBar>
      </form>
    </main>
  );
}
